// ============================================================
// DockerNetworking.jsx
// ============================================================
// Junior-dev Notes:
// - Concept: Containers are isolated by default. Networks are the "wires".
// - Key Term: "Service Discovery" (Connecting via container name, not IP).
// - Strategy: Visualize the 'Docker Bridge' as a virtual router.
// - Branding: prodevconsult.com
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import RouterIcon from "@mui/icons-material/Router";
import LanIcon from "@mui/icons-material/Lan";
import PublicIcon from "@mui/icons-material/Public";
import DnsIcon from "@mui/icons-material/Dns";
import HubIcon from "@mui/icons-material/Hub";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SecurityIcon from "@mui/icons-material/Security";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#0DB7ED";
const NAVY_BG = "#001E2B";
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.6)",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(13, 183, 237, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const NetworkNode = styled(Paper)(({ theme, active }) => ({
    padding: theme.spacing(3),
    backgroundColor: active ? "rgba(13, 183, 237, 0.1)" : "rgba(255,255,255,0.02)",
    border: `1px solid ${active ? DOCKER_BLUE : BRAND.border}`,
    borderRadius: "12px",
    textAlign: "center",
    transition: "0.3s",
    "&:hover": {
        transform: "scale(1.05)",
        borderColor: DOCKER_BLUE
    }
}));

export default function DockerNetworking() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="Connectivity · Module 08"
                            sx={{ bgcolor: "rgba(13, 183, 237, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Docker <span style={{ color: DOCKER_BLUE }}>Networking</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        How do containers talk to each other? Docker creates virtual networks
                        that allow isolated apps to communicate securely using DNS names.
                    </Typography>
                </Box>

                {/* 2. THE VIRTUAL BRIDGE VISUAL */}
                <Box sx={{ mb: 10, position: 'relative', p: 4, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 4 }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 6, fontWeight: 800, color: DOCKER_BLUE }}>
                        The Default Bridge Architecture
                    </Typography>

                    <Grid container spacing={4} justifyContent="center" alignItems="center">
                        <Grid item xs={12} md={3}>
                            <NetworkNode>
                                <Box sx={{ color: DOCKER_BLUE, mb: 1 }}><HubIcon /></Box>
                                <Typography variant="subtitle2" fontWeight={700}>App Container</Typography>
                                <Typography variant="caption" display="block" color={BRAND.muted}>IP: 172.17.0.2</Typography>
                            </NetworkNode>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Stack alignItems="center">
                                <RouterIcon sx={{ fontSize: 60, color: DOCKER_BLUE, mb: 1, opacity: 0.8 }} />
                                <Typography variant="button" sx={{ letterSpacing: 2, color: DOCKER_BLUE }}>Docker0 Bridge</Typography>
                                <Divider sx={{ width: '100%', bgcolor: DOCKER_BLUE, my: 1, height: 2 }} />
                                <Typography variant="caption">Virtual Switch</Typography>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <NetworkNode>
                                <Box sx={{ color: DOCKER_BLUE, mb: 1 }}><DnsIcon /></Box>
                                <Typography variant="subtitle2" fontWeight={700}>DB Container</Typography>
                                <Typography variant="caption" display="block" color={BRAND.muted}>IP: 172.17.0.3</Typography>
                            </NetworkNode>
                        </Grid>
                    </Grid>
                </Box>

                {/* 3. NETWORK DRIVERS */}
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 6, textAlign: "center" }}>
                    Common <span style={{ color: DOCKER_BLUE }}>Network Drivers</span>
                </Typography>

                <Grid container spacing={3} sx={{ mb: 10 }}>
                    {[
                        {
                            title: "Bridge (Default)",
                            icon: <LanIcon />,
                            desc: "The standard for standalone containers. It creates a private network on the host so containers can communicate."
                        },
                        {
                            title: "Host",
                            icon: <RouterIcon />,
                            desc: "Removes network isolation between the container and the Docker host. Use this for maximum performance."
                        },
                        {
                            title: "None",
                            icon: <SecurityIcon />,
                            desc: "Completely disables networking for the container. Perfect for high-security batch processing jobs."
                        },
                        {
                            title: "Overlay",
                            icon: <PublicIcon />,
                            desc: "Connects multiple Docker daemons together. This is used in Docker Swarm for multi-host networking."
                        }
                    ].map((driver, i) => (
                        <Grid item xs={12} sm={6} md={3} key={i}>
                            <Paper sx={{
                                p: 3,
                                height: '100%',
                                bgcolor: "rgba(255,255,255,0.02)",
                                border: `1px solid ${BRAND.border}`,
                                textAlign: 'center',
                                borderRadius: 3
                            }}>
                                <Box sx={{ color: DOCKER_BLUE, mb: 2 }}>{driver.icon}</Box>
                                <Typography variant="subtitle1" fontWeight={800} gutterBottom>{driver.title}</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, fontSize: '0.85rem', lineHeight: 1.6 }}>
                                    {driver.desc}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* 4. SERVICE DISCOVERY (The Pro Concept) */}
                <Paper sx={{
                    p: 4,
                    bgcolor: "rgba(13, 183, 237, 0.05)",
                    borderLeft: `5px solid ${DOCKER_BLUE}`,
                    borderRadius: "0 16px 16px 0"
                }}>
                    <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: DOCKER_BLUE }}>
                        💡 Service Discovery: Stop using IPs!
                    </Typography>
                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 3 }}>
                        In a custom bridge network, Docker provides an internal DNS server.
                        If your database container is named <code>db_prod</code>, your app can connect to it using:
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: "#011627", borderRadius: 2, border: "1px solid rgba(173, 219, 103, 0.2)" }}>
                        <Typography sx={{ fontFamily: 'monospace', color: "#ADDB67", fontSize: '0.9rem' }}>
                            DATABASE_URL=mongodb://db_prod:27017
                        </Typography>
                    </Box>
                </Paper>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 5. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>Ready to orchestrate? Let's meet Docker Compose.</Typography>
                    <Link to="/docker-compose" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 8,
                                py: 2,
                                borderRadius: "4px",
                                boxShadow: `0 4px 14px rgba(13, 183, 237, 0.3)`,
                                "&:hover": { bgcolor: "#0091ca" }
                            }}
                        >
                            Next: Docker Compose
                        </Button>
                    </Link>
                    <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.3 }}>
                        Authorized Content by {BRAND.domain}
                    </Typography>
                </Box>

            </Container>
        </PageWrapper>
    );
}
